// components/report/Report.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { REASONS } from "./createReport";
import "./Report.scss";

export default function Report({
  reportTitle = "Report message",
  onClose,
  onSubmit,
  followUpQuestion = "",
  followUpButtons = [],
}) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const dialog = useRef(null);

  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    return () => element.close();
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!reason || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await onSubmit(reason, details);
      setSent(true);
    } catch {
      setError("Could not send your report. Please try again.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return createPortal(
    <dialog
      ref={dialog}
      className="report-modal"
      aria-label={reportTitle}
      onCancel={(event) => {
        event.preventDefault();
        if (!submitting.current) onClose();
      }}
    >
      <form className="report-form" onSubmit={submit}>
        <h3>{sent ? "Report received" : reportTitle}</h3>

        {sent ? (
          <>
            <p role="status">
              Thank you for reporting this. We’ll review it and take appropriate
              action if it violates our community rules.
            </p>
            {followUpQuestion && <p>{followUpQuestion}</p>}
            <div className="report-actions" style={{ flexWrap: "wrap" }}>
              {followUpButtons.map((button, index) => (
                <button
                  key={button.key ?? index}
                  type="button"
                  disabled={button.disabled}
                  onClick={button.onPress}
                >
                  {button.label}
                </button>
              ))}
              <button type="button" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <fieldset disabled={busy}>
              <legend>Why are you reporting this?</legend>
              {REASONS.map((value) => (
                <label className="report-reason" key={value}>
                  <input
                    type="radio"
                    name="reportReason"
                    value={value}
                    checked={reason === value}
                    onChange={() => setReason(value)}
                  />
                  {value}
                </label>
              ))}
              <label className="report-details">
                Additional details (optional)
                <textarea
                  value={details}
                  maxLength={2000}
                  rows={3}
                  onChange={(event) => setDetails(event.target.value)}
                />
              </label>
            </fieldset>
            {error && <p className="report-error" role="alert">{error}</p>}
            <div className="report-actions">
              <button type="button" disabled={busy} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" disabled={!reason || busy}>
                {busy ? "Sending…" : "Report"}
              </button>
            </div>
          </>
        )}
      </form>
    </dialog>,
    document.body,
  );
}